import {
  ApprovalForm,
  type ApprovalFormFields,
} from "@/components/ApprovalForm/ApprovalForm";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import { useModal } from "@/hooks/modals/useModal";
import { useFlow } from "@/hooks/services/useFlow";
import { SrmPurchaseContractDTO, SupplierBlockItemDetail } from "@/types/apis";
import { Space, type FormInstance } from "antd";
import { notifySuccess, notifyWarning } from "@/utils/notification";
import type { PurchaseContractFormValues } from "../type";
import { useContractRecordQuery } from "./useContractRecordQuery";
import {
  CONTRACT_CATEGORY_OPTIONS_PO,
  CONTRACT_CATEGORY_OPTIONS,
  CONTRACT_SUB_CATEGORY_OPTIONS,
} from "@/constants";
import { useContractFlowNodeMatcher } from "./useContractFlowNodeMatcher";
import { useTranslation } from "react-i18next";
import { ContractCheck } from "../components/ContractCheck/ContractCheck";
import { extractFormFieldError } from "@/utils/form.helper";

export function useFormFooter(
  form: FormInstance<PurchaseContractFormValues>,
  cleanup: () => void,
) {
  const { t } = useTranslation("contractManagement");
  const { props, open } = useModal("PURCHASE_CONTRACT");
  const {
    recordId,
    onSuccess,
    mode,
    taskId,
    flowStatus,
    viewStatus,
    isReadOnly,
  } = props;

  const handleSuccess = (message: string) => {
    notifySuccess(message);
    onSuccess?.();
    cleanup();
  };

  const detail = useContractRecordQuery(recordId);

  const isCreated = open && !recordId;
  const isRecreate = open && !isReadOnly && flowStatus === "已驳回";

  const { flowCode, startFlowMutation, passFlowMutation, mutationLoading } =
    useFlow("PURCHASE_CONTRACT", isCreated || isRecreate);

  const {
    isMatchConfirmContractNode,
    isMatchPendingSupplierUploadNode,
    isMatchSupplierContractUploadShouldApplyNode,
  } = useContractFlowNodeMatcher(detail?.insId);

  // 暂存
  const handleTemporarySubmit = async () => {
    const values = form.getFieldsValue();

    if (!isRecreate && detail) {
      passFlowMutation.mutate(
        {
          taskId: detail.taskId,
          flowParamsDto: {
            flowStatus: 0,
            skipType: "PASS",
            message: "",
            flowCode: "PURCHASE_CONTRACT",
            variable: {
              supplierUserId: values.supplierId,
            },
            srmPurchaseContractDTO: {
              ...buildDto(detail.processingNumber, values),
              id: detail.id,
            },
          },
        },
        {
          onSuccess: () => handleSuccess(t("暂存已提交")),
        },
      );
      return;
    }

    if (!flowCode) return;

    startFlowMutation.mutate(
      {
        flowCode: "PURCHASE_CONTRACT",
        formNo: flowCode,
        flowStatus: 0,
        variable: {
          supplierUserId: values.supplierId,
        },
        srmPurchaseContractDTO: buildDto(flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("暂存已提交")),
      },
    );
  };

  // 提交
  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }
    const values = form.getFieldsValue();

    if (recordId && detail && !isRecreate) {
      passFlowMutation.mutate(
        {
          taskId: detail.taskId,
          flowParamsDto: {
            flowStatus: 1,
            skipType: "PASS",
            message: "",
            flowCode: "PURCHASE_CONTRACT",
            variable: {
              supplierUserId: values.supplierId,
            },
            srmPurchaseContractDTO: {
              ...buildDto(detail.processingNumber, values),
              id: detail.id,
            },
          },
        },
        {
          onSuccess: () => handleSuccess(t("申请已提交")),
        },
      );
      return;
    }

    if (!flowCode) return;

    startFlowMutation.mutate(
      {
        flowCode: "PURCHASE_CONTRACT",
        formNo: flowCode,
        flowStatus: 1,
        variable: {
          supplierUserId: values.supplierId,
        },
        srmPurchaseContractDTO: buildDto(flowCode, values),
      },
      {
        onSuccess: () => handleSuccess(t("申请已提交")),
      },
    );
  };

  // 上级审批
  const handleLeaderApproval = (values: ApprovalFormFields) => {
    if (!taskId || !detail) return;

    passFlowMutation.mutate(
      {
        taskId: taskId,
        flowParamsDto: {
          skipType: values.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: values.message,
          flowCode: "PURCHASE_CONTRACT",
          variable: {},
          srmPurchaseContractDTO: {
            id: detail.id,
            rejectReason: values.skipType === "REJECT" ? values.message : "",
          },
        },
      },
      {
        onSuccess: () => handleSuccess(t("审批成功")),
      },
    );
  };

  // 申请人提交合同（上传已签署合同）
  const handleContractUpload = async (approvalValues: ApprovalFormFields) => {
    if (!taskId || !detail) return;

    const values = await form.validateFields();

    passFlowMutation.mutate(
      {
        taskId: taskId,
        flowParamsDto: {
          skipType: approvalValues.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: approvalValues.message,
          flowCode: "PURCHASE_CONTRACT",
          variable: {},
          srmPurchaseContractDTO: {
            ...buildDto(detail.processingNumber, values),
            contractUploader: detail.contractUploader,
            id: detail.id,
            rejectReason:
              approvalValues.skipType === "REJECT"
                ? approvalValues.message
                : "",
          },
        },
      },
      {
        onSuccess: () => handleSuccess(t("提交成功")),
      },
    );
  };

  // 供应商发起合同审查，第一次审批
  const handleApproveSupplierContractUpload = handleContractUpload;

  // 财务审批（财务填写kms编号）
  const handleFinanceApproval = async () => {
    if (!taskId || !detail) return;

    const values = await form.validateFields();

    passFlowMutation.mutate(
      {
        taskId: taskId,
        flowParamsDto: {
          skipType: "PASS", // 驳回时传空处理
          message: "",
          flowCode: "PURCHASE_CONTRACT",
          variable: {},
          srmPurchaseContractDTO: {
            ...buildDto(detail.processingNumber, values),
            id: detail.id,
            contractUploader: detail.contractUploader,
            kmsContractNumber: values.kmsContractNumber,
          },
        },
      },
      {
        onSuccess: () => handleSuccess(t("提交成功")),
      },
    );
  };

  const renderFooter = (detail?: SupplierBlockItemDetail) => {
    const Footer = {
      submitApply: (
        <Space>
          <ContractCheck form={form} />
          <PrimaryButton
            loading={mutationLoading}
            onClick={handleTemporarySubmit}
          >
            {t("暂存")}
          </PrimaryButton>
          <PrimaryButton loading={mutationLoading} onClick={handleSubmit}>
            {t("提交")}
          </PrimaryButton>
        </Space>
      ),
      close: (
        <Space>
          <ContractCheck form={form} />
          <PrimaryOutlineButton onClick={cleanup}>
            {t("关闭")}
          </PrimaryOutlineButton>
        </Space>
      ),
      approve: (
        <ApprovalForm
          loading={mutationLoading}
          onApprovalSubmit={handleLeaderApproval}
          leftExtend={<ContractCheck form={form} />}
        />
      ),
      contractUpload: (
        <ApprovalForm
          loading={mutationLoading}
          onApprovalSubmit={handleContractUpload}
          leftExtend={<ContractCheck form={form} />}
        />
      ),
      approveSupplierContractUpload: (
        <ApprovalForm
          loading={mutationLoading}
          onApprovalSubmit={handleApproveSupplierContractUpload}
          leftExtend={<ContractCheck form={form} />}
        />
      ),
      contractConfirm: (
        <Space>
          <ContractCheck form={form} />
          <PrimaryButton
            loading={mutationLoading}
            onClick={handleFinanceApproval}
          >
            {t("提交")}
          </PrimaryButton>
        </Space>
      ),
    };

    const closeRules = [
      mode === "started" && flowStatus === "审批中", // 申请人-审批中
      mode === "started" && flowStatus === "已驳回" && isReadOnly, // 申请人-审批驳回
      mode === "started" && flowStatus === "已存档", // 申请人-审批通过
      mode === "inReview" && viewStatus === false, // 审批人-审批任务待办已处理
    ];

    if (closeRules.some((r) => r)) {
      return Footer.close;
    }

    if (mode === "inReview" && isMatchPendingSupplierUploadNode)
      return Footer.contractUpload;
    if (mode === "inReview" && isMatchSupplierContractUploadShouldApplyNode)
      return Footer.contractUpload;
    if (mode === "inReview" && isMatchConfirmContractNode)
      return Footer.contractConfirm;

    if (mode === "inReview" && flowStatus === "审批中") return Footer.approve;

    return Footer.submitApply;
  };

  return renderFooter;
}

function buildDto(
  flowCode: string,
  values: PurchaseContractFormValues,
): SrmPurchaseContractDTO {
  const fileIds = extractFileIds(values);

  return {
    contractUploader: "澳美",
    processingNumber: flowCode,
    purchaserUserName: values.username,
    purchaserUserUsername: values.employeeCode,
    deptName: values.department,
    postName: values.postName,
    purchaserUserEmail: values.email,
    supplierUserId: values.supplierId?.toString(),
    supplierUserCode: values.supplierCode,
    supplierUserName: values.supplierNameZh,
    supplierUsUserName: values.supplierNameEn,

    contractName: values.contractName,
    contractNumber: values.contractNumber,
    currency: values.currency,
    rate: values.rate,
    inputAmount: values.inputAmount,
    amountHkd: values.amountHkd,
    contractValidity: values.contractValidity?.format("YYYY-MM-DD"),
    contractContent: values.contractContent,

    userId: values.applicantId,
    userName: values.applicantName,
    userNickName: values.applicantNickname,
    userDept: values.departmentName,
    userDeptManager: values.departmentManagerName,
    factory: values.factory,
    contractSource: values.contractSource,

    purchaseRequestId: values.approvedPurchaseApplyId,
    purchaseRequestNumber: values.approvedPurchaseApplyNo?.toString(),

    materialTypeValue: values.materialType,
    materialTypeName:
      CONTRACT_CATEGORY_OPTIONS.find(
        (option) => option.value === values.materialType,
      )?.label || "",
    contractCategoryValue: values.contractCategory,
    contractCategoryName:
      CONTRACT_CATEGORY_OPTIONS_PO.find(
        (option) => option.value === values.contractCategory,
      )?.label || "",
    contractSubcategoryValue: values.contractSubCategory,
    contractSubcategoryName:
      CONTRACT_SUB_CATEGORY_OPTIONS.find(
        (option) => option.value === values.contractSubCategory,
      )?.label || "",
    contractDescriptionValue: values.contractDescription,
    contractDescriptionName: values.contractDescription,

    fileIds,

    contractReviewItems: JSON.stringify(values.contractReviewItems),
  };
}

function extractFileIds(values: PurchaseContractFormValues): string[] {
  const attachmentIds = values.attachments?.map((file) => file.uid) || [];
  const contractFileIds = values.contractFiles?.map((file) => file.uid) || [];

  const relatedFiles = values.relatedFiles?.map((file) => file.uid) || [];

  const signedContracts = values.signedContracts?.map((file) => file.uid) || [];
  const finalContractRelatedAttachments =
    values.finalContractRelatedAttachments?.map((file) => file.uid) || [];

  return [
    ...attachmentIds,
    ...contractFileIds,
    ...relatedFiles,
    ...signedContracts,
    ...finalContractRelatedAttachments,
  ];
}
